import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { fmtDate, fmtEur, fmtNumber, translator, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { projectsMessages } from '@/i18n/messages/projects'
import { projectStatus } from './status'

export interface ProjectRequest {
  requestId: string
  createdAt: string
  createdByName: string | null
  status: 'open' | 'keyed_in'
  projectName: string
  workingName: string | null
  codeInternal: string | null
  codeExternal: string | null
  providerName: string | null
  romName: string | null
  projectManagerName: string | null
  auStatus: number | null
  country: string | null
  calcType: number | null
  planingStart: string | null
  planingEnd: string | null
  tbStart: string | null
  tbEnd: string | null
  planedMetres: number | null
  planedHp: number | null
  contractValue: number | null
  projectValue: number | null
  priceListNote: string | null
  contactName: string | null
  contractNumber: string | null
  description: string | null
  note: string | null
}

type ProjectsT = ReturnType<typeof translator<keyof typeof projectsMessages.en>>

const CALC_KEYS: Record<number, 'calc1' | 'calc2' | 'calc3' | 'calc4'> = { 1: 'calc1', 2: 'calc2', 3: 'calc3', 4: 'calc4' }
const calcLabel = (t: ProjectsT, code: number) => (CALC_KEYS[code] ? t(CALC_KEYS[code]!) : String(code))

/** A plain-text block of everything set, for pasting straight into AU. */
function asText(r: ProjectRequest): string {
  const t = translator(projectsMessages)
  const fmt = fmtDate
  const st = r.auStatus != null ? `${r.auStatus} · ${projectStatus(r.auStatus).label}` : ''
  const lines: [string, string | null | undefined][] = [
    [t('projectName'), r.projectName],
    [t('workingName'), r.workingName],
    [t('codeInternal'), r.codeInternal],
    [t('codeExternal'), r.codeExternal],
    [t('status'), st],
    [t('country'), r.country],
    [t('provider'), r.providerName],
    [t('rolloutManager'), r.romName],
    [t('projectManager'), r.projectManagerName],
    [t('calcType'), r.calcType != null ? calcLabel(t, r.calcType) : ''],
    [t('priceList'), r.priceListNote],
    [t('planning'), r.planingStart || r.planingEnd ? `${fmt(r.planingStart)} – ${fmt(r.planingEnd)}` : ''],
    ['TB', r.tbStart || r.tbEnd ? `${fmt(r.tbStart)} – ${fmt(r.tbEnd)}` : ''],
    [t('plannedMetres'), r.planedMetres != null ? fmtNumber(r.planedMetres) : ''],
    [t('plannedHp'), r.planedHp != null ? fmtNumber(r.planedHp) : ''],
    [t('contractValue'), r.contractValue != null ? fmtEur(r.contractValue) : ''],
    [t('projectValue'), r.projectValue != null ? fmtEur(r.projectValue) : ''],
    [t('contact'), r.contactName],
    [t('contractNo'), r.contractNumber],
    [t('description'), r.description],
    [t('note'), r.note],
  ]
  return lines.filter(([, v]) => v && String(v).trim() !== '')
    .map(([k, v]) => `${k}: ${v}`).join('\n')
}

export function ProjectRequests({ requests }: { requests: ProjectRequest[] }) {
  const t = useT(projectsMessages)
  if (requests.length === 0) {
    // The bold action sits mid-sentence; where depends on the language.
    const [before, after] = t('noRequests').split('{action}')
    return (
      <div className="rounded-2xl bg-white p-10 text-center text-sm text-neutral-500 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        {before}<b>{t('addProjectAction')}</b>{after}
      </div>
    )
  }
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {requests.map((r) => <RequestCard key={r.requestId} r={r} />)}
    </div>
  )
}

function RequestCard({ r }: { r: ProjectRequest }) {
  const t = useT(projectsMessages)
  const tc = useT(commonMessages)
  const fmt = fmtDate
  const qc = useQueryClient()
  const [copied, setCopied] = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)
  const done = r.status === 'keyed_in'

  const invalidate = () => qc.invalidateQueries({ queryKey: ['project-requests'] })
  const markDone = useMutation({ mutationFn: () => api.post(`/api/projects/requests/${r.requestId}/done`), onSuccess: invalidate })
  const remove = useMutation({ mutationFn: () => api.del(`/api/projects/requests/${r.requestId}`), onSuccess: invalidate })

  const copy = async () => {
    try { await navigator.clipboard.writeText(asText(r)); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch { /* no clipboard */ }
  }

  return (
    <div className={`flex flex-col rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800 ${done ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate font-semibold">{r.projectName}</h3>
          <p className="mt-0.5 text-xs text-neutral-500">
            {[r.codeExternal, r.codeInternal].filter(Boolean).join(' · ') || '—'}
            {r.createdByName ? t('byName', { name: r.createdByName }) : ''} · {fmt(r.createdAt)}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
          done ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300'
               : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
        }`}>{done ? t('keyedIn') : t('open')}</span>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        <Row k={t('provider')} v={r.providerName} />
        <Row k="ROM" v={r.romName} />
        <Row k={t('status')} v={r.auStatus != null ? projectStatus(r.auStatus).label : null} />
        <Row k={t('calcType')} v={r.calcType != null ? calcLabel(t, r.calcType) : null} />
        <Row k={t('plannedM')} v={r.planedMetres != null ? fmtNumber(r.planedMetres) : null} />
        <Row k={t('contractEur')} v={r.contractValue != null ? fmtNumber(r.contractValue) : null} />
        <Row k="TB" v={r.tbStart || r.tbEnd ? `${fmt(r.tbStart)} – ${fmt(r.tbEnd)}` : null} />
        <Row k={t('priceList')} v={r.priceListNote} />
      </dl>
      {r.note && <p className="mt-2 rounded-lg bg-neutral-50 p-2 text-xs text-neutral-600 dark:bg-neutral-800/60 dark:text-neutral-300">{r.note}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-3 dark:border-neutral-800">
        <button onClick={copy}
                className="rounded-md px-2 py-1 text-xs font-medium text-neutral-700 ring-1 ring-neutral-300 hover:bg-neutral-100 dark:text-neutral-200 dark:ring-neutral-700 dark:hover:bg-neutral-800">
          {copied ? t('copied') : t('copyDetails')}
        </button>
        {!done && (
          <button onClick={() => markDone.mutate()} disabled={markDone.isPending}
                  className="rounded-md bg-brand-600 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-500 disabled:opacity-60">
            {t('markKeyedIn')}
          </button>
        )}
        {confirmDel ? (
          <span className="ml-auto flex items-center gap-1.5 text-xs">
            <span className="text-neutral-500">{t('deleteConfirm')}</span>
            <button onClick={() => remove.mutate()} disabled={remove.isPending}
                    className="rounded-md px-2 py-1 font-semibold text-white" style={{ backgroundColor: 'var(--viz-critical)' }}>{tc('yes')}</button>
            <button onClick={() => setConfirmDel(false)}
                    className="rounded-md px-2 py-1 text-neutral-600 ring-1 ring-neutral-300 hover:bg-neutral-100 dark:text-neutral-300 dark:ring-neutral-700">{tc('no')}</button>
          </span>
        ) : (
          <button onClick={() => setConfirmDel(true)}
                  className="ml-auto rounded-md px-2 py-1 text-xs text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200">
            {tc('delete')}
          </button>
        )}
      </div>
    </div>
  )
}

function Row({ k, v }: { k: string; v: string | null }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-neutral-400">{k}</dt>
      <dd className={`truncate text-right ${v ? '' : 'text-neutral-300 dark:text-neutral-600'}`}>{v ?? '—'}</dd>
    </div>
  )
}
